import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePassLivenessCheck, useValidateInvitationCode } from '@/hooks/queries/useAuthQueries';
import { useAuthStore } from '@/stores/authStore';
import { isLivenessVerified } from '@/utils/isVerified';
import { isBorrower, isInvestor } from '@/utils/user';
import { Button } from '../ui/Button';
import { StepSeparatorPlus } from './StepSeparatorPlus';
import { VerificationStepCard, VerificationStepCardStatus } from './VerificationStepCard';

type UserVerificationContentProps = {
  onComplete: () => void;
  onSkip?: () => void;
};

enum UserVerificationStep {
  INVITATION_CODE = 'invitation_code',
  LIVENESS_CHECK = 'liveness_check',
}

// TODO: fix use memo dependencies
export const UserVerificationContent: React.FC<UserVerificationContentProps> = ({ onComplete, onSkip }) => {
  const { user, isLivenessChecked } = useAuthStore();
  const [invitationCode, setInvitationCode] = useState('');
  const { mutateAsync: validateInvitationCodeMutation, isPending: isValidateInvitationCodePending } =
    useValidateInvitationCode();
  const { mutateAsync: passLivenessCheckMutation, isPending: isPassLivenessCheckPending } = usePassLivenessCheck();
  const [error, setError] = useState<string | null>(null);

  const passedLivenessCheck = isLivenessVerified(user);
  const needsLivenessCheck = !passedLivenessCheck;

  const hasInvitationCode = !!user?.has_invitation_code;
  const passedInvitationCode = hasInvitationCode;
  const needsInvitationCode = !passedInvitationCode && isInvestor(user);

  const validateInvitationCode = invitationCode.length === 6;

  useEffect(() => {
    if (isLivenessChecked && needsLivenessCheck) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError('Identification check failed');
    }
  }, [isLivenessChecked, needsLivenessCheck]);

  const handleInvitationCodeChange = useCallback(
    (value: string) => {
      setInvitationCode(value);
      if (error) {
        setError(null);
      }
    },
    [error]
  );

  const steps = useMemo(() => {
    if (!user) return [];
    const invitationCodeStatus = hasInvitationCode
      ? VerificationStepCardStatus.SUCCESS
      : isBorrower(user)
        ? VerificationStepCardStatus.OPTIONAL
        : VerificationStepCardStatus.PENDING;

    const isInvitationDisabled = hasInvitationCode || (isBorrower(user) && !user.is_liveness_verified);
    const showInvitationInput = !hasInvitationCode || (isBorrower(user) && user.is_liveness_verified);

    const livenessCheckStatus = needsLivenessCheck
      ? VerificationStepCardStatus.PENDING
      : VerificationStepCardStatus.SUCCESS;
    const livenessCheckDisabled =
      passedLivenessCheck || (isInvestor(user) && needsInvitationCode && !hasInvitationCode);

    const steps = [
      {
        type: UserVerificationStep.LIVENESS_CHECK,
        icon: '/photo_csv.svg',
        iconAlt: 'Identification check',
        status: livenessCheckStatus,
        title: 'Identification check',
        description: "To verify that you're a human.",
        disabled: livenessCheckDisabled,
        showInput: false,
      },
      {
        type: UserVerificationStep.INVITATION_CODE,
        icon: '/deposit.svg',
        iconAlt: 'Deposit or qualification code',
        status: invitationCodeStatus,
        title: 'Qualification code',
        description: 'Enter the Qualification code received',
        disabled: isInvitationDisabled,
        showInput: showInvitationInput,
        inputValue: invitationCode,
        onInputChange: handleInvitationCodeChange,
      },
    ];
    return isBorrower(user) ? steps : steps.reverse();
  }, [
    user,
    passedLivenessCheck,
    invitationCode,
    handleInvitationCodeChange,
    needsLivenessCheck,
    needsInvitationCode,
    hasInvitationCode,
  ]);

  const currentStep = useMemo(() => {
    if (!user) return null;
    if (isInvestor(user)) {
      if (passedInvitationCode) {
        return steps[1]; // Identification check
      }
      return steps[0]; // qualification code
    } else {
      if (passedLivenessCheck) {
        return steps[1]; // qualification code
      }
      return steps[0]; // Identification check
    }
  }, [user, passedInvitationCode, passedLivenessCheck, steps]);

  if (!user) {
    return null;
  }

  const handlePassLivenessCheck = async () => {
    try {
      await passLivenessCheckMutation();
    } catch (error) {
      console.error(error);
      setError('Failed to pass Identification check');
    }
  };

  const handlePassInvitationCode = async () => {
    if (!validateInvitationCode) {
      return;
    }

    try {
      await validateInvitationCodeMutation({ invitation_code: invitationCode });
    } catch (error) {
      console.error(error);
      setError('Invalid qualification code');
    }
  };

  const mainText = isBorrower(user)
    ? 'To complete your account verification, please complete the steps below.'
    : 'To start investing, please complete the steps below.';
  const descriptionText = isBorrower(user)
    ? 'Please pass the Identification check to complete your account verification.'
    : 'Please enter the qualification code and pass the Identification check to start investing.';

  const submitButtonLoading = isValidateInvitationCodePending || isPassLivenessCheckPending;
  const submitButtonText =
    currentStep?.type === UserVerificationStep.LIVENESS_CHECK
      ? 'Pass Identification check'
      : 'Validate qualification code';

  const handleClick = () => {
    if (!currentStep) {
      return;
    }

    switch (currentStep.type) {
      case UserVerificationStep.INVITATION_CODE:
        handlePassInvitationCode();
        break;
      case UserVerificationStep.LIVENESS_CHECK:
        handlePassLivenessCheck();
        break;
    }
  };

  const showStartButton = passedInvitationCode && passedLivenessCheck;
  return (
    <div className="w-full flex flex-col gap-5 px-25 pt-15 pb-6 overflow-y-auto max-h-[60vh]">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-black text-center leading-relaxed font-bold font-avenir">{mainText}</p>
        <p className="text-sm text-gray-700 text-center leading-relaxed font-medium font-avenir">{descriptionText}</p>
      </div>

      <div className="flex flex-col items-center gap-4 justify-center sm:flex-row sm:items-start">
        {steps.map((step, index) => (
          <React.Fragment key={step.type}>
            <div className="flex flex-col items-center gap-2 flex-1">
              <VerificationStepCard
                icon={step.icon}
                iconAlt={step.iconAlt}
                status={step.status}
                title={step.title}
                description={step.description}
                disabled={step.disabled}
                showInput={step.showInput}
                inputValue={step.inputValue}
                onInputChange={step.onInputChange}
              />
            </div>
            {index < steps.length - 1 && (
              <div className="self-center">
                <StepSeparatorPlus />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="flex justify-center">
          <p className="text-sm text-red-500 font-avenir">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex justify-center">
          {showStartButton ? (
            <Button onClick={onComplete} variant="primary">
              Start {isBorrower(user) ? 'borrowing' : 'investing'}
            </Button>
          ) : currentStep ? (
            <div className="flex flex-col">
              <Button onClick={handleClick} variant="primary" loading={submitButtonLoading}>
                {submitButtonText}
              </Button>
              {currentStep?.type === UserVerificationStep.INVITATION_CODE && (
                <p className="text-sm text-gray-700 text-center font-avenir mt-2">
                  If you don't have a code, contact a{' '}
                  <span
                    className="underline cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      window.open('/authorized-agents', '_blank');
                    }}
                  >
                    Trusted Agent
                  </span>
                </p>
              )}
            </div>
          ) : null}
        </div>
        {!showStartButton && (
          <div className="flex justify-center">
            <Button
              onClick={onSkip || (() => {})}
              variant="text"
              className="text-sm text-gray-600 hover:text-gray-800 underline cursor-pointer font-avenir"
            >
              {isBorrower(user) && passedLivenessCheck ? 'Skip' : 'Skip for now'} &gt;
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
